import React, { useState } from "react";
import {
  Button,
  Modal,
  Box,
  Typography,
  Divider,
  Stack,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import moment from "moment";
import EnhancedTextField from "./editor";
import FileUploader from "./editor";
import Swal from "sweetalert2";

const AddWorkButtonWithModal = ({
  taxpayerList,
  workerList,
  onSave,
  workTypeOptions,
  workStatusOptions,
}) => {
  // Modal state'i
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form state'leri
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);

  const [status, setStatus] = useState("NOT_DONE");
  const [workerId, setWorkerId] = useState(null);
  const [taxpayerId, setTaxpayerId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(moment());
  const [workType, setWorkType] = useState(null);

  // Modal'ı aç
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // Modal'ı kapat
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTitle("");
    setDescription(""); // Form state'lerini sıfırla
    setFiles([]);
    setStatus("NOT_DONE");
    setWorkerId(null);
    setTaxpayerId(null);
    setExpiresAt(moment());
    setWorkType(null);
  };

  // Kaydet butonuna basıldığında
  const handleSave = () => {
    if (!title && !description) {
      Swal.fire({
        icon: "warning",
        title: "Uyarı",
        text: "Başlık ve Açıklama aynı anda boş bırakılamaz!",
        confirmButtonText: "Tamam",
      });
      return; // Swal açıldıktan sonra fonksiyonun devamını engelle
    }
    const newWork = {
      title,
      description,
      files,
      status,
      workerId,
      taxpayerId,
      workType: workType || null,
      expiresAt: moment(expiresAt).endOf("day"), // Moment'ı ISO string'e çevir
    };
    console.log(newWork);
    onSave(newWork); // Yeni işi dışarıya iletiyoruz
    handleCloseModal(); // Modal'ı kapat
  };

  return (
    <>
      {/* İş Ekle Butonu */}
      <Button
        className="custom-button"
        variant="contained"
        startIcon={<NoteAddIcon />} // İkonu ekledik
        sx={{
          textTransform: "none",
          whiteSpace: "nowrap",
          backgroundColor: "#4CAF50 !important", // Güzel bir yeşil renk
          "&:hover": { backgroundColor: "#388E3C !important" }, // Hover efekti için koyu yeşil
        }}
        onClick={handleOpenModal}
      >
        Not Ekle
      </Button>
      {/* Modal */}
      <Modal
        open={isModalOpen}
        onClose={(event, reason) => {
          // Sadece butonla kapatılabilsin, dışarı tıklayınca kapanmasın
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseModal();
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: "70vw", md: "50vw", lg: "25vw" },
            maxHeight: "80vh", // Yükseklik sınırı
            overflowY: "auto", // Scrollbar eklensin
            overflowX: "hidden",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            fontWeight="bold"
            gutterBottom
          >
            Yeni Not Ekle
          </Typography>

          <Divider sx={{ my: 2 }} />
          {/* Mükellef */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2, width: "100%" }}
          >
            <Autocomplete
              fullWidth
              value={
                taxpayerList.find((item) => item.id === taxpayerId) || {
                  id: "OTHER",
                  title: "Diğer",
                } // Varsayılan olarak "Diğer" seçili
              }
              onChange={(event, newValue) => {
                if (newValue?.id === "OTHER") {
                  setTaxpayerId(null); // "Diğer" seçildiğinde taxpayerId'yi null yap
                } else {
                  setTaxpayerId(newValue ? newValue.id : null); // Diğer seçenekler için id'yi ayarla
                }
              }}
              options={[{ id: "OTHER", title: "Diğer" }, ...taxpayerList] || []} // "Diğer" seçeneğini başa ekle
              getOptionLabel={(option) => option?.title || ""} // Sadece title göster
              isOptionEqualToValue={(option, value) => option.id === value.id} // ID'yi karşılaştır
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mükellef"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Stack>
          {/* Başlık */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TextField
              label="Başlık"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>

          {/* Açıklama */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2, width: "100%" }}
          >
            <FileUploader
              description={description}
              setDescription={setDescription}
              files={files}
              setFiles={setFiles}
            />
          </Stack>
          {/* İş Türü */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2, width: "70%" }}
          >
            <Autocomplete
              fullWidth
              value={
                workTypeOptions.find((item) => item.id === workType) || {
                  id: "DIGER",
                  title: "Diğer",
                } // Varsayılan olarak "Diğer" seçili
              }
              onChange={(event, newValue) => {
                if (newValue && newValue?.id === "EVRAK") {
                  setStatus("DONE");
                } else {
                  if (status === "DONE") {
                    setStatus("NOT_DONE");
                  }
                }
                setWorkType(newValue ? newValue.id : null);
                // Diğer seçenekler için id'yi ayarla
              }}
              options={workTypeOptions || []} // "Diğer" seçeneğini başa ekle
              getOptionLabel={(option) => option?.title || ""} // Sadece title göster
              isOptionEqualToValue={(option, value) => option.id === value.id} // ID'yi karşılaştır
              renderInput={(params) => (
                <TextField {...params} label="İş Türü" fullWidth size="small" />
              )}
            />
          </Stack>

          {/* Sorumlu Kişi */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2, width: "70%" }}
          >
            <Autocomplete
              fullWidth
              value={workerList.find((item) => item.id === workerId) || null} // Seçili değeri bul
              onChange={(event, newValue) => {
                setWorkerId(newValue ? newValue.id : null); // Sadece id'yi set et
              }}
              options={workerList || []}
              getOptionLabel={(option) => option?.title || ""} // Sadece title göster
              isOptionEqualToValue={(option, value) => option.id === value.id} // ID'yi karşılaştır
              noOptionsText="Kayıtlı Kişi Bulunamadı."
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sorumlu Kişi"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Stack>
          {/**İş Durumu */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2, width: "70%" }}
          >
            <Autocomplete
              fullWidth
              value={
                workStatusOptions.find((item) => item.id === status) ||
                workStatusOptions.find((item) => item.id === "NOT_DONE")
              } // Seçili değeri bul
              onChange={(event, newValue) => {
                setStatus(newValue ? newValue.id : null); // Sadece id'yi set et
              }}
              options={workStatusOptions || []}
              disabled={workType === "EVRAK"}
              disableClearable
              getOptionLabel={(option) => option?.title || ""} // Sadece title göster
              isOptionEqualToValue={(option, value) => option.id === value.id} // ID'yi karşılaştır
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="İş Durumu"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Stack>

          {/* Bitiş Tarihi */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Bitiş Tarihi"
                value={expiresAt}
                onChange={(newValue) => {
                  console.log(moment(newValue).endOf("day"));
                  setExpiresAt(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </LocalizationProvider>
          </Stack>

          {/* Kaydet Butonu */}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-around" }}>
            <Button variant="outlined" color="error" onClick={handleCloseModal}>
              Kapat
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Kaydet
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AddWorkButtonWithModal;
